import React, { useMemo, useState } from "react";
import { Dropdown } from "antd";
import type { DropDownProps, MenuProps } from "antd";
import { Check, ChevronDown } from "lucide-react";
import { Container } from "../HtmlTags/Container/Container";
import { Span } from "../HtmlTags/Span/Span";
import "./CustomDropdown.css";
import { Option } from "../../../types";

export interface CustomDropdownProps extends DropDownProps {
  options: Option[];
  defaultLabel?: string;
  placeholder?: string;
  selectedValue?: string;
  selectedValues?: string[];
  onSelect?: (value: string, label?: string) => void;
  className?: string;
  error?: boolean;
  multiple?: boolean;
  ariaLabel?: string;
}

export const CustomDropdown: React.FC<CustomDropdownProps> = ({
  options,
  defaultLabel = "Category",
  placeholder,
  selectedValue,
  selectedValues = [],
  onSelect,
  className,
  error,
  multiple = false,
  ariaLabel,
  ...rest
}) => {
  const [open, setOpen] = useState(false);

  const triggerText = useMemo(() => {
    if (multiple) {
      if (selectedValues.length === 0) {
        return placeholder ?? defaultLabel;
      }
      const labels = selectedValues
        .map((v) => options.find((o) => o.value === v)?.label)
        .filter((x): x is string => Boolean(x));
      if (labels.length === 0) {
        return placeholder ?? defaultLabel;
      }
      if (labels.length === 1) {
        return labels[0];
      }
      if (labels.length === 2) {
        return `${labels[0]}, ${labels[1]}`;
      }
      return `${labels[0]}, ${labels[1]} + ${labels.length - 2} more`;
    }
    if (selectedValue !== undefined && selectedValue !== "") {
      const option = options.find((opt) => opt.value === selectedValue);
      if (option) {
        return option.label;
      }
    }
    return defaultLabel;
  }, [
    multiple,
    selectedValues,
    options,
    selectedValue,
    defaultLabel,
    placeholder,
  ]);

  const hasSelection = multiple
    ? selectedValues.length > 0
    : Boolean(
      selectedValue &&
      options.some((opt) => opt.value === selectedValue)
    );

  const isSelected = (value: string) => {
    if (multiple) {
      return selectedValues.includes(value);
    }
    return selectedValue === value;
  };

  const items: MenuProps["items"] = options.map((option) => {
    const selected = isSelected(option.value);
    return {
      key: option.value,
      label: (
        <Span
          className={`dropdown-item ${multiple ? "dropdown-item--multi" : ""} ${selected ? "selected" : ""}`}
          onClick={() => {
            onSelect?.(option.value, option.label);
          }}
        >
          {multiple && (
            <Span className="dropdown-item-check" aria-hidden>
              {selected ? (
                <Check className="dropdown-item-check-icon" size={15} strokeWidth={2.5} />
              ) : null}
            </Span>
          )}
          <Span className="dropdown-item-text">{option.label}</Span>
        </Span>
      ),
    };
  });

  const triggerClassName = [
    "custom-dropdown",
    className || "",
    error ? "error-border" : "",
    multiple ? "custom-dropdown--multiple" : "",
    hasSelection ? "custom-dropdown--filled" : "custom-dropdown--empty",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Dropdown
      menu={{ items }}
      trigger={["click"]}
      open={open}
      onOpenChange={setOpen}
      {...rest}
    >
      <Container
        className={triggerClassName}
        role="button"
        tabIndex={0}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel ? `${ariaLabel}: ${triggerText}` : triggerText}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen((v) => !v);
          }
        }}
      >
        <Span className="label">{triggerText}</Span>
        <ChevronDown
          className={`icon ${open ? "rotated" : ""}`}
          height={20}
          width={20}
          aria-hidden
        />
      </Container>
    </Dropdown>
  );
};
